import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Pagination
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FETCH_ROLES_REQUEST } from "../../redux/Roles/RoleTypes/RoleTypes";
import {
  deleteRoleRequest,
  resetRoleState
} from "../../redux/Roles/actions/RolesActions";
import {
  ADD_ROLE,
  EDIT_ROLE,
  VIEW_ROLE
} from "../../component/common/Routes/AppRoutes";
import { saveAs } from "file-saver";
import "../../styles/RolePermissionsList.css";
import ReusableRoleTable from "./ReusableRoleTable";
import SearchAndButtonTab from "../../component/ui/SearchAndButtonTab";
import nodata from "../../assets/nodata.jpg";
import { columnFetcher } from "../../Style";
import { toast, ToastContainer } from "react-toastify";
import Loading from "../../component/ui/Loading";
import CustomButton from "../../component/ui/FormFields/Button";

export default function RolePermissionsList() {
  const user = useSelector((state) => state.auth.user);
 const location = useLocation();
     
      const pageNumber= location.state?.page ?? 1;
  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === "string"
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const rolePermissions = {
    canAdd: permissions?.roles?.add ?? false,
    canView: permissions?.roles?.view ?? false,
    canEdit: permissions?.roles?.edit ?? false,
    canDelete: permissions?.roles?.delete ?? false,
    hasAllAccess: permissions?.roles?.allaccess ?? false
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [searchColumn, setSearchColumn] = useState("name");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(pageNumber);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);

  const { roles, success, error } = useSelector((state) => state.roles);
  const role = useSelector((state) => state.roles);
  const viewListLoading = useSelector((state) => state.roles?.loading);
  const deleteLoading = role?.deleteLoading;

  useEffect(() => {
    dispatch({ type: FETCH_ROLES_REQUEST });
  }, [dispatch]);
  useEffect(() => {
    if (success) {
      toast.success("Role Deleted Successfully", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(resetRoleState());
        }
      });
    }
  }, [success]);

  const permissionsList = [
    "add",
    "view",
    "edit",
    "delete",
    "allaccess",
    "noaccess"
  ];

  const roleFilterHeaders = [
    { value: "name", label: "Role Name" },
    { value: "serial_no", label: "Role ID" }
  ];

  const filteredRoles = useMemo(() => {
    if (!roles) return [];

    return roles.filter((role) => {
      if (!searchQuery.trim()) return true;

      const columnValue = role?.[searchColumn];

      if (columnValue === null || columnValue === undefined) return false;

      const searchValue = String(columnValue).toLowerCase().trim();
      const query = searchQuery.toLowerCase().trim();

      if (searchColumn === "category") {
        return searchValue === query;
      }
      return searchValue.includes(query);
    });
  }, [roles, searchQuery, searchColumn]);

  const paginatedRoles = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredRoles.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRoles, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };
  const handleDeleteClick =
    rolePermissions.canDelete || user?.user_type === 1
      ? (id) => {
          setRoleToDelete(id);
          setOpenDialog(true);
        }
      : undefined;

  const handleConfirmDelete = () => {
    dispatch(deleteRoleRequest(roleToDelete));
    setOpenDialog(false);
    setRoleToDelete(null);
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
    setRoleToDelete(null);
  };

  const handleView =
    rolePermissions.canView || user?.user_type === 1
      ? (role) =>
          navigate(`${VIEW_ROLE.replace(":id", role.id)}`, {
            state: { roleData: role,page:page }
          })
      : undefined;

  const handleEdit =
    rolePermissions.canEdit || user?.user_type === 1
      ? (role) =>
          navigate(`${EDIT_ROLE.replace(":id", role.id)}`, {
            state: { roleData: role ,pageEdit:page}
          })
      : undefined;

  const handleExport = () => {
    const BOM = "\uFEFF";

    const csvContent =
      BOM +
      [
        ["Role Name", "Category", ...permissionsList],
        ...filteredRoles.flatMap((role) =>
          Object.entries(role.permissions).map(([category, permissions]) => [
            role.name,
            category,
            ...permissionsList.map((perm) =>
              permissions?.[perm] ? "Yes" : "No"
            )
          ])
        )
      ]
        .map((row) =>
          row
            .map((field) => {
              if (field && typeof field === "string" && field.includes(",")) {
                return `"${field}"`;
              }
              return field;
            })
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "roles_permissions.csv");
  };
  const handleUploadCSV = (file) => {
    if (!file) return;
  };

  const columns = useMemo(
    () => (filteredRoles.length > 0 ? columnFetcher(filteredRoles) : [[], []]),
    [filteredRoles]
  );
  if (viewListLoading) {
    return <Loading text="Loading Role Details..." />;
  }

  return (
    <>
      <ToastContainer />
      <Box className="container">
        <SearchAndButtonTab
          title={"Roles"}
          columns={roleFilterHeaders}
          searchColumn={searchColumn}
          setSearchColumn={setSearchColumn}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleUploadCSV={handleUploadCSV}
          handleExport={handleExport}
          routeName={ADD_ROLE}
          permissions={{
            ...permissions,
            roles: rolePermissions
          }}
          user={user}
        />

        {loading ? (
          <Box textAlign="center" py={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            py={5}
          >
            <img
              src={nodata}
              alt="No Data"
              style={{
                maxWidth: "300px",
                maxHeight: "300px"
              }}
            />
          </Box>
        ) : (
          // <Typography variant="h6" align="center" color="error">
          //   {typeof error === "string" ? error : JSON.stringify(error)}
          // </Typography>
          <>
            <ReusableRoleTable
              roles={paginatedRoles.map((role) => ({
                ...role,
                permissions:
                  typeof role.permissions === "string"
                    ? JSON.parse(role.permissions)
                    : role.permissions || {}
              }))}
              permissionsList={permissionsList}
              handleView={handleView}
              handleEdit={handleEdit}
              handleDeleteClick={handleDeleteClick}
            />
            '
            {filteredRoles.length > 0 && (
              <Box className="button-box">
                <Pagination
                  count={Math.ceil(filteredRoles.length / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
        <Dialog open={openDialog} onClose={handleCancelDelete}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this role
              <strong>{roleToDelete?.name}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={handleCancelDelete}
              className="handle-back"
            >
              Cancel
            </Button>

            <CustomButton
              variant="contained"
              className="handle-delete"
              loading={deleteLoading}
              size={"small"}
              onClick={handleConfirmDelete}
              text={"Delete"}
            />
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
